
import React, { useEffect, useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { AgeGroupVariationFormData } from "../schemas/ageGroupVariationSchema";
import { GradeOption } from '../hooks/useAgeGroupVariationForm';
import { RequiredFieldLabel } from '@/components/ui/required-field-label';

interface MinAgeSectionProps {
  form: UseFormReturn<AgeGroupVariationFormData>;
  gradeOptions: GradeOption[];
}

export const MinAgeSection: React.FC<MinAgeSectionProps> = ({
  form,
  gradeOptions
}) => {
  const showAgeFirst = !form.watch('showMinAgeGradeAsPrimary');
  
  // Use state to preserve values during position swapping
  const [minAgeValue, setMinAgeValue] = useState<number | null>(form.getValues().minAgeInYears);
  const [minGradeValue, setMinGradeValue] = useState<string>(form.getValues().minAgeGradeKey || "none");
  const [minAgeNote, setMinAgeNote] = useState<string>(form.getValues().minAgeNote || "");
  
  // Update local state when form values change
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.minAgeInYears !== undefined) {
        setMinAgeValue(value.minAgeInYears);
      }
      if (value.minAgeGradeKey !== undefined) {
        setMinGradeValue(value.minAgeGradeKey || "none");
      }
      if (value.minAgeNote !== undefined) {
        setMinAgeNote(value.minAgeNote || "");
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);
  
  // Force update form values whenever local state or position changes
  useEffect(() => {
    // Only update the form if the values actually exist
    if (minAgeValue !== undefined) {
      form.setValue('minAgeInYears', minAgeValue, { shouldDirty: true, shouldValidate: true });
    }
    
    if (minGradeValue !== undefined) {
      const gradeKey = minGradeValue === "none" ? "" : minGradeValue;
      form.setValue('minAgeGradeKey', gradeKey, { shouldDirty: true, shouldValidate: true });
    }
    
    if (minAgeNote !== undefined) {
      form.setValue('minAgeNote', minAgeNote, { shouldDirty: true, shouldValidate: true });
    }
  }, [showAgeFirst, minAgeValue, minGradeValue, minAgeNote, form]);
  
  // Create controlled components to preserve values regardless of order
  const renderMinAgeField = () => (
    <FormField
      control={form.control}
      name="minAgeInYears"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">
            {showAgeFirst 
              ? <RequiredFieldLabel label="Min age, in years" />
              : "Min age, in years (optional if using grade)"}
          </FormLabel>
          <FormControl>
            <Input
              type="number"
              min={0}
              max={20}
              step="0.1" // Allow decimal values like 1.5
              value={minAgeValue !== null ? minAgeValue : ''}
              onChange={e => {
                const newValue = e.target.value ? Number(e.target.value) : null;
                setMinAgeValue(newValue);
                field.onChange(newValue);
              }}
              placeholder="Enter age"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderMinGradeField = () => (
    <FormField
      control={form.control}
      name="minAgeGradeKey"
      rules={{
        validate: (value) => {
          // If Age is primary, always allow any value
          if (showAgeFirst) return true;
          
          // If Grade is primary, require a non-empty value that isn't "none"
          return value !== undefined && value !== "" && value !== null;
        }
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">
            {showAgeFirst 
              ? "Min grade (optional if using years old)" 
              : <RequiredFieldLabel label="Min grade" />}
          </FormLabel>
          <Select
            onValueChange={value => {
              setMinGradeValue(value);
              field.onChange(value === "none" ? "" : value);
            }}
            value={minGradeValue || "none"}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {gradeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <div className="space-y-4 border-r pr-4 pl-1">
      <div className="flex items-center gap-2 mb-4">
        <FormLabel htmlFor="showMinAgeGradeAsPrimary" className="text-left block whitespace-nowrap">
          Show min age based on:
        </FormLabel>
        <FormField
          control={form.control}
          name="showMinAgeGradeAsPrimary"
          render={({ field }) => (
            <Select
              onValueChange={value => {
                field.onChange(value === "true");
              }}
              value={field.value ? "true" : "false"}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">Years old</SelectItem>
                <SelectItem value="true">Grade</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showAgeFirst ? (
        <>
          {renderMinAgeField()}
          {renderMinGradeField()}
        </>
      ) : (
        <>
          {renderMinGradeField()}
          {renderMinAgeField()}
        </>
      )}

      <FormField
        control={form.control}
        name="minAgeNote"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-left block">Min age footnote (optional)</FormLabel>
            <FormControl>
              <Input 
                value={minAgeNote}
                onChange={e => {
                  setMinAgeNote(e.target.value);
                  field.onChange(e.target.value);
                }}
                placeholder=""
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
